import { z } from 'zod';

import { makeTypescriptEnumSchema } from '../../enumSchemas';
import objectPassthrough from '../../objectPassthrough';
import { WithUnknownValuePlaceholder } from '../../unknownValuePlaceholder';
import { ProtectedValue, Unprotect, UnprotectedValue } from '../ProtectedValue';

export enum Fido2HmacCredentialAlgorithm {
  hmacSha256 = 'hmac-sha256',
}

// Reference: https://fidoalliance.org/specs/cx/cxf-v1.0-ps-20250814.html#dict-fido2-hmac-credentials
interface PasskeyHmacCredentials {
  algorithm: WithUnknownValuePlaceholder<Fido2HmacCredentialAlgorithm, undefined>;
  credWithUV: ProtectedValue;
  credWithoutUV: ProtectedValue;
}
const PasskeyHmacCredentials = objectPassthrough({
  algorithm: makeTypescriptEnumSchema(Fido2HmacCredentialAlgorithm, undefined),
  credWithUV: ProtectedValue,
  credWithoutUV: ProtectedValue,
});

type UnprotectedPasskeyHmacCredentials = Unprotect<PasskeyHmacCredentials, UnprotectedValue>;
const UnprotectedPasskeyHmacCredentials = objectPassthrough({
  algorithm: makeTypescriptEnumSchema(Fido2HmacCredentialAlgorithm, undefined),
  credWithUV: UnprotectedValue,
  credWithoutUV: UnprotectedValue,
});

type UnprotectedStringPasskeyHmacCredentials = Unprotect<PasskeyHmacCredentials, string>;
const UnprotectedStringPasskeyHmacCredentials = objectPassthrough({
  algorithm: makeTypescriptEnumSchema(Fido2HmacCredentialAlgorithm, undefined),
  credWithUV: z.string(),
  credWithoutUV: z.string(),
});

interface PasskeyLargeBlob {
  uncompressedSize: number;
  data: ProtectedValue;
}

const PasskeyLargeBlob = objectPassthrough({
  uncompressedSize: z.number(),
  data: ProtectedValue,
});

interface UnprotectedPasskeyLargeBlob {
  uncompressedSize: number;
  data: UnprotectedValue;
}

const UnprotectedPasskeyLargeBlob = objectPassthrough({
  uncompressedSize: z.number(),
  data: UnprotectedValue,
});

interface UnprotectedStringPasskeyLargeBlob {
  uncompressedSize: number;
  data: string;
}

const UnprotectedStringPasskeyLargeBlob = objectPassthrough({
  uncompressedSize: z.number(),
  data: z.string(),
});

interface PasskeyFido2Extensions {
  hmacCredentials?: PasskeyHmacCredentials;
  credBlob?: ProtectedValue;
  largeBlob?: PasskeyLargeBlob;
  payments?: boolean;
}
const PasskeyFido2Extensions = objectPassthrough({
  hmacCredentials: PasskeyHmacCredentials.optional(),
  credBlob: ProtectedValue.optional(),
  largeBlob: PasskeyLargeBlob.optional(),
  payments: z.boolean().optional(),
});

interface UnprotectedPasskeyFido2Extensions {
  hmacCredentials?: UnprotectedPasskeyHmacCredentials;
  credBlob?: UnprotectedValue;
  largeBlob?: UnprotectedPasskeyLargeBlob;
  payments?: boolean;
}
const UnprotectedPasskeyFido2Extensions = objectPassthrough({
  hmacCredentials: UnprotectedPasskeyHmacCredentials.optional(),
  credBlob: UnprotectedValue.optional(),
  largeBlob: UnprotectedPasskeyLargeBlob.optional(),
  payments: z.boolean().optional(),
});

interface UnprotectedStringPasskeyFido2Extensions {
  hmacCredentials?: UnprotectedStringPasskeyHmacCredentials;
  credBlob?: string;
  largeBlob?: UnprotectedStringPasskeyLargeBlob;
  payments?: boolean;
}
const UnprotectedStringPasskeyFido2Extensions = objectPassthrough({
  hmacCredentials: UnprotectedStringPasskeyHmacCredentials.optional(),
  credBlob: z.string().optional(),
  largeBlob: UnprotectedStringPasskeyLargeBlob.optional(),
  payments: z.boolean().optional(),
});

// The legacy field is dropped even if hmacCredentials is already present, so a passkey never carries
// the same credentials in two places.
function transformHmacSecretToFido2Extensions<
  T extends { hmacSecret?: S; fido2Extensions?: { hmacCredentials?: S } },
  S,
>(pk: T) {
  if (!pk.hmacSecret) {
    return pk;
  }
  const { hmacSecret, ...rest } = pk;
  if (pk.fido2Extensions?.hmacCredentials) {
    return rest;
  }
  return {
    ...rest,
    fido2Extensions: { ...pk.fido2Extensions, hmacCredentials: hmacSecret },
  };
}

// Reference: https://fidoalliance.org/specs/cx/cxf-v1.0-ps-20250814.html#dict-passkey
interface Passkey {
  // NOTE: We omit the passkey type, as it is implicitly set by the way we store passkeys in a login.
  // The type field should be added back during export.
  // type: 'passkey';
  credentialId: string;
  rpId: string;
  username: string;
  userDisplayName: string;
  // Base64Url encoded
  userHandle: string;
  // Base64Url encoded
  key: ProtectedValue;
  fido2Extensions?: PasskeyFido2Extensions;
  // Set by `moveInvalidFido2Extensions`
  unmigratedFido2Extensions?: UnprotectedStringPasskeyFido2Extensions;
  // This is a cache of all related origins on which this passkey should also be
  // displayed besides the rpId and its registrable domain suffixes. We update
  // this cache whenever the passkey is used. Note that this key should be
  // stripped during export.
  relatedOrigins?: string[];
}
const BasePasskey = objectPassthrough({
  credentialId: z.string(),
  rpId: z.string(),
  username: z.string(),
  userDisplayName: z.string(),
  // Base64Url encoded
  userHandle: z.string(),
  // Base64Url encoded
  key: ProtectedValue,
  // Exists only for backwards compatibility, moved into fido2Extensions by
  // `transformHmacSecretToFido2Extensions` during parse
  hmacSecret: PasskeyHmacCredentials.optional(),
  fido2Extensions: PasskeyFido2Extensions.optional(),
  unmigratedFido2Extensions: UnprotectedStringPasskeyFido2Extensions.optional(),
  relatedOrigins: z.array(z.string()).optional(),
});
// CXF imports before fido2Extensions was modeled stored the extensions verbatim, with plain strings
// where (Un)ProtectedValues are expected. Parsing can't encrypt them, so if they match
// `unmigratedSchema` they are kept aside instead of failing the whole vault. Otherwise they are
// dropped.
function moveInvalidFido2Extensions(
  pk: unknown,
  schema: z.ZodObject,
  moveStringFido2ExtensionsToUnmigrated?: boolean,
) {
  if (typeof pk !== 'object' || pk === null || !('fido2Extensions' in pk)) {
    return pk;
  }
  if (schema.optional().safeParse(pk.fido2Extensions).success) {
    return pk;
  }
  const { fido2Extensions, ...rest } = pk;
  if (moveStringFido2ExtensionsToUnmigrated) {
    const unmigratedSchemaParseResult =
      UnprotectedStringPasskeyFido2Extensions.safeParse(fido2Extensions);
    if (unmigratedSchemaParseResult.success) {
      return { ...rest, unmigratedFido2Extensions: unmigratedSchemaParseResult.data };
    }
  }
  return rest;
}

const Passkey = z.preprocess(
  (pk) => moveInvalidFido2Extensions(pk, PasskeyFido2Extensions, true),
  BasePasskey.overwrite(transformHmacSecretToFido2Extensions),
);

// The unprotected version of the Passkey with an UnprotectedValue as its key.
// We need this for UnprotectedLogins.ts.
type UnprotectedPasskey = Unprotect<Passkey, UnprotectedValue>;
// No migration path exists for unprotected passkeys, so invalid fido2Extensions are dropped.
const UnprotectedPasskey = z.preprocess(
  (pk) => moveInvalidFido2Extensions(pk, UnprotectedPasskeyFido2Extensions),
  BasePasskey.omit({
    key: true,
    hmacSecret: true,
    fido2Extensions: true,
  })
    .extend({
      key: UnprotectedValue,
      hmacSecret: UnprotectedPasskeyHmacCredentials.optional(),
      fido2Extensions: UnprotectedPasskeyFido2Extensions.optional(),
    })
    .overwrite(transformHmacSecretToFido2Extensions),
);

// Accessible version of the Passkey with a string as key
type UnprotectedStringPasskey = Unprotect<Passkey, string>;
const UnprotectedStringPasskey = BasePasskey.omit({
  key: true,
  hmacSecret: true,
  fido2Extensions: true,
})
  .extend({
    key: z.string(),
    hmacSecret: UnprotectedStringPasskeyHmacCredentials.optional(),
    fido2Extensions: UnprotectedStringPasskeyFido2Extensions.optional(),
  })
  .overwrite(transformHmacSecretToFido2Extensions);

export {
  Passkey,
  UnprotectedPasskey,
  UnprotectedStringPasskey,
  PasskeyHmacCredentials,
  UnprotectedPasskeyHmacCredentials,
  UnprotectedStringPasskeyHmacCredentials,
  PasskeyFido2Extensions,
  UnprotectedPasskeyFido2Extensions,
  UnprotectedStringPasskeyFido2Extensions,
};
